// Case costs — PI Platform outline v2 §3.18.
//
// Advanced-cost receivables: the firm fronts the money and recovers it
// out of the settlement. Sits under Liens on the Compliance tab because
// both are claims against the same pot, and both feed the disbursement.
//
// Two things this has to get right, and they pull in opposite
// directions:
//
//   Entry has to be fast. A cost recorded months later is a cost that
//   gets recorded wrong or not at all, and an unrecorded advance is
//   money the firm silently ate. So the add row is always visible —
//   no "Add" button to click first.
//
//   The three totals have to stay distinct. What the firm laid out,
//   what it will recover, and what it absorbed are different numbers,
//   and collapsing them hides where the money went.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const money = (v) =>
  v === null || v === undefined ? "—" : `$${Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Calendar date, not an instant — see the note in LiensCard.
const dateOnly = (v) => (v ? String(v).slice(0, 10) : "");

const EMPTY = { description: "", amount: "", category: "", payee: "", is_recoverable: true };

export function CaseCostsCard({ matterId, showNotification }) {
  const [costs, setCosts] = useState(null);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch(`/api/matters/${matterId}/case-costs`);
      setCosts(d.data || []);
      setSummary(d.summary || null);
    } catch (e) {
      showNotification?.(`Case costs failed to load: ${e.message}`, "error");
      setCosts([]);
    }
  }, [matterId, showNotification]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiFetch("/api/cost-categories")
      .then(d => setCategories(d.data || []))
      .catch(() => setCategories([]));
  }, []);

  const add = async () => {
    if (!draft.description.trim() || !draft.amount) {
      showNotification?.("A cost needs a description and an amount.", "warning");
      return;
    }
    setSaving(true);
    try {
      await apiFetch(`/api/matters/${matterId}/case-costs`, {
        method: "POST",
        body: JSON.stringify({
          description: draft.description.trim(),
          amount: Number(draft.amount),
          category: draft.category || null,
          payee: draft.payee.trim() || null,
          is_recoverable: draft.is_recoverable,
        }),
      });
      setDraft(EMPTY);
      await load();
    } catch (e) {
      showNotification?.(`Couldn't record that cost: ${e.message}`, "error");
    }
    setSaving(false);
  };

  const patch = async (id, changes) => {
    try {
      await apiFetch(`/api/case-costs/${id}`, { method: "PATCH", body: JSON.stringify(changes) });
      await load();
    } catch (e) {
      showNotification?.(`Update failed: ${e.message}`, "error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this cost? It stops being recoverable from the settlement.")) return;
    try {
      await apiFetch(`/api/case-costs/${id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, "error");
    }
  };

  const writeOff = (c) => {
    const raw = window.prompt(
      `Write off how much of ${money(c.amount)}?\n\n` +
      "This reduces what comes out of the client's settlement and stays " +
      "on the record as a decision.");
    if (!raw) return;
    const amount = Number(raw);
    if (!(amount > 0)) { showNotification?.("Enter an amount above zero.", "warning"); return; }
    if (amount > c.amount) {
      showNotification?.("You can't write off more than the cost itself.", "warning");
      return;
    }
    const reason = window.prompt("Why? (kept with the write-off)") || null;
    patch(c.id, { written_off_amount: amount, write_off_reason: reason });
  };

  if (costs === null) {
    return React.createElement("div", { style: cardStyle },
      React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px" } },
        React.createElement(Spinner, { size: 16 }),
        React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } }, "Loading case costs…")));
  }

  const inp = { padding: "7px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                fontSize: "13px", background: "#fff" };

  return React.createElement("div", { style: cardStyle },
    React.createElement("h2", { style: h2Style }, "Case Costs"),
    React.createElement("p", {
      style: { fontSize: "13px", color: C.textMuted, margin: "4px 0 14px" },
    }, costs.length === 0
        ? "Nothing advanced on this matter yet. Costs recorded here are deducted from the settlement automatically."
        : `${costs.length} cost${costs.length === 1 ? "" : "s"} advanced · deducted from the settlement automatically`),

    // Three totals, kept distinct.
    summary && costs.length > 0 && React.createElement("div", {
      style: {
        display: "flex", gap: "22px", flexWrap: "wrap", padding: "12px 14px",
        background: C.warmWhite, borderRadius: "8px", marginBottom: "14px",
      },
    },
      _stat("Advanced", money(summary.advanced_total), "everything the firm laid out"),
      _stat("Recoverable", money(summary.recoverable_total), "comes off the settlement"),
      _stat("Firm absorbs", money(summary.absorbed_total), "written off or non-recoverable"),
    ),

    // Always-visible add row — a cost recorded later is recorded wrong.
    React.createElement("div", {
      style: {
        display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center",
        padding: "12px 14px", background: C.warmWhite, borderRadius: "8px", marginBottom: "14px",
      },
    },
      React.createElement("select", {
        value: draft.category, onChange: e => setDraft({ ...draft, category: e.target.value }),
        style: { ...inp, minWidth: "160px" },
      },
        React.createElement("option", { value: "" }, "Category…"),
        ...categories.map(c => React.createElement("option", { key: c.id, value: c.name }, c.name))),
      React.createElement("input", {
        type: "text", value: draft.description, placeholder: "What was it for",
        onChange: e => setDraft({ ...draft, description: e.target.value }),
        style: { ...inp, flex: "1 1 200px" },
      }),
      React.createElement("input", {
        type: "text", value: draft.payee, placeholder: "Paid to (optional)",
        onChange: e => setDraft({ ...draft, payee: e.target.value }),
        style: { ...inp, width: "170px" },
      }),
      React.createElement("input", {
        type: "number", value: draft.amount, placeholder: "0.00", step: "0.01",
        onChange: e => setDraft({ ...draft, amount: e.target.value }),
        onKeyDown: e => { if (e.key === "Enter") add(); },
        style: { ...inp, width: "110px" },
      }),
      React.createElement("label", {
        title: "Uncheck for costs the firm absorbs by policy — they stay off the client's deduction.",
        style: { display: "flex", gap: "5px", alignItems: "center", fontSize: "12px", color: C.textMuted, cursor: "pointer" },
      },
        React.createElement("input", {
          type: "checkbox", checked: draft.is_recoverable,
          onChange: e => setDraft({ ...draft, is_recoverable: e.target.checked }),
        }), "Recoverable"),
      React.createElement("button", {
        onClick: add, disabled: saving,
        style: {
          padding: "8px 16px", borderRadius: "8px", border: "none",
          background: saving ? C.warmGray : C.gold, color: "#fff",
          fontSize: "12px", fontWeight: 700, cursor: saving ? "default" : "pointer",
        },
      }, saving ? "Saving…" : "Record"),
    ),

    costs.length > 0 && React.createElement("div", {
      style: { border: `1px solid ${C.warmGray}60`, borderRadius: "8px", overflow: "hidden" },
    },
      ...costs.map((c, i) => React.createElement("div", {
        key: c.id,
        style: {
          padding: "10px 13px", borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}40`,
          display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
          background: c.is_recoverable ? "#fff" : C.warmWhite,
        },
      },
        React.createElement("div", { style: { flex: "1 1 220px", minWidth: 0 } },
          React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } },
            c.description),
          React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" } },
            [c.category, c.payee, dateOnly(c.incurred_date)].filter(Boolean).join(" · "),
            !c.is_recoverable ? " · firm absorbs this" : "",
          ),
          c.written_off_amount ? React.createElement("div", {
            style: { fontSize: "11px", color: C.navy, marginTop: "3px" },
          }, `${money(c.written_off_amount)} written off`
             + (c.write_off_reason ? ` — ${c.write_off_reason}` : "")) : null,
        ),

        React.createElement("div", { style: { fontSize: "12px", whiteSpace: "nowrap", textAlign: "right" } },
          React.createElement("div", { style: { color: C.textDark } }, money(c.amount)),
          // Only worth showing when it differs from the amount —
          // otherwise it is the same number twice.
          c.recoverable_balance !== c.amount && React.createElement("div", {
            style: { fontSize: "11px", color: C.textMuted },
          }, `${money(c.recoverable_balance)} recoverable`),
        ),

        c.is_recoverable && !c.written_off_amount && _quiet("Write off", () => writeOff(c)),
        _quiet("Remove", () => remove(c.id)),
      )),
    ),
  );
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};

function _stat(label, value, hint) {
  return React.createElement("div", { key: label, title: hint },
    React.createElement("div", {
      style: { fontSize: "10px", color: "#9A948C", fontWeight: 700,
               textTransform: "uppercase", letterSpacing: "0.06em" },
    }, label),
    React.createElement("div", { style: { fontSize: "16px", fontWeight: 700, color: "#1B2A4A", marginTop: "2px" } },
      value),
    React.createElement("div", { style: { fontSize: "10px", color: "#9A948C", marginTop: "1px" } }, hint));
}

function _quiet(label, onClick) {
  return React.createElement("button", {
    key: label, onClick,
    style: {
      border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
      padding: "5px 11px", fontSize: "11px", fontWeight: 600,
      color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
    },
  }, label);
}
